/**
 * Visuell profil — editorial settings page.
 *
 * Layout: numbered two-column sections (label + prose on the left,
 * controls on the right) under a serif display title. The drop-zone
 * and live letterhead are the load-bearing visual moments.
 *
 * Logo upload saves immediately because there's no intermediate state
 * to commit.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type ReactNode,
} from 'react';
import { Check, ImagePlus, Loader2, Plus, Star } from 'lucide-react';
import { tenantApi, type SelfParty } from '../api.js';
import { useTenantSession } from '../auth-context.js';
import { useBranding } from '../branding-context.js';
import { LetterheadPreview } from '../components/LetterheadPreview.js';
import { useInvalidate, useTenantQuery } from '../query.js';
import {
  ALLOWED_LOGO_TYPES,
  MAX_LOGO_BYTES,
  fileToDataUrl,
} from '../branding-utils.js';
import { usePageTitle } from '../use-page-title.js';

const SERIF = '"Georgia", "Times New Roman", serif';

// Matches the sidebar's fallback so the on_primary plate previews the
// logo on the surface it actually renders against.
const SIDEBAR_PRIMARY_FALLBACK = '#1A3D2B';

type LogoMeta = {
  type: string;
  bytes: number;
};

/** Identifies which of the two logo variants the user is editing. */
type LogoVariant = 'on_white' | 'on_primary';

const LOGO_VARIANTS: Array<{
  variant: LogoVariant;
  title: string;
  blurb: string;
  /** Backend patch key for this variant. */
  patchKey: 'brand_logo_on_white_url' | 'brand_logo_on_primary_url';
}> = [
  {
    variant: 'on_white',
    title: 'På vit bakgrund',
    blurb: 'Visas i sidhuvudet och på dokumentmallarna. Vanligtvis er mörka eller fullfärgsvariant.',
    patchKey: 'brand_logo_on_white_url',
  },
  {
    variant: 'on_primary',
    title: 'På primärfärg',
    blurb: 'Visas i sidomenyn — på er primärfärg. Vanligtvis er ljusa / vita variant. Vi inverterar inte automatiskt.',
    patchKey: 'brand_logo_on_primary_url',
  },
];

type SelfPartyForm = {
  label: string;
  name: string;
  orgNumber: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  country: string;
  email: string;
  phone: string;
};

const EMPTY_SELF_PARTY: SelfPartyForm = {
  label: '',
  name: '',
  orgNumber: '',
  addressLine1: '',
  addressLine2: '',
  postalCode: '',
  city: '',
  country: 'SE',
  email: '',
  phone: '',
};

function selfPartyToForm(sp: SelfParty | null): SelfPartyForm {
  if (!sp) return EMPTY_SELF_PARTY;
  return {
    label: sp.label ?? '',
    name: sp.name,
    orgNumber: sp.orgNumber ?? '',
    addressLine1: sp.addressLine1 ?? '',
    addressLine2: sp.addressLine2 ?? '',
    postalCode: sp.postalCode ?? '',
    city: sp.city ?? '',
    country: sp.country ?? 'SE',
    email: sp.email ?? '',
    phone: sp.phone ?? '',
  };
}

export function SettingsPage() {
  usePageTitle('Inställningar');
  const { token, actor } = useTenantSession();
  const { branding, refresh } = useBranding();
  const isOrgAdmin = actor?.tenantRole === 'org_admin';

  const [logoMeta, setLogoMeta] = useState<Record<LogoVariant, LogoMeta | null>>({
    on_white: null,
    on_primary: null,
  });
  const [err, setErr] = useState<string | null>(null);
  const [uploadingVariant, setUploadingVariant] = useState<LogoVariant | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const fileInputOnWhite = useRef<HTMLInputElement>(null);
  const fileInputOnPrimary = useRef<HTMLInputElement>(null);
  const inputRefFor = (v: LogoVariant) =>
    v === 'on_white' ? fileInputOnWhite : fileInputOnPrimary;

  // Self-party identities — the landlord identities contracts autofill
  // from. Multi-row: a tenant can keep several (e.g. one per portfolio
  // company) and mark one as default. Each row is created/edited/promoted
  // independently in <SelfPartiesManager>.

  const selfPartiesQ = useTenantQuery(
    'settings.list_self_parties',
    () => tenantApi.settings.list_self_parties(),
    [],
    { ignoreActiveGroup: true },
  );
  const invalidate = useInvalidate();

  useEffect(() => {
    if (selfPartiesQ.error) setErr((selfPartiesQ.error as Error).message);
  }, [selfPartiesQ.error]);

  const flashSaved = useCallback(() => {
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 1600);
  }, []);

  const handleFile = useCallback(
    async (variant: LogoVariant, f: File) => {
      if (!token) return;
      setErr(null);
      setUploadingVariant(variant);
      try {
        const dataUrl = await fileToDataUrl(f);
        const patchKey =
          variant === 'on_white' ? 'brand_logo_on_white_url' : 'brand_logo_on_primary_url';
        await tenantApi.branding.update({ [patchKey]: dataUrl });
        setLogoMeta((prev) => ({ ...prev, [variant]: { type: f.type, bytes: f.size } }));
        await refresh();
        flashSaved();
      } catch (ex) {
        setErr((ex as Error).message);
      } finally {
        setUploadingVariant(null);
        const ref = inputRefFor(variant);
        if (ref.current) ref.current.value = '';
      }
    },
    [token, refresh, flashSaved],
  );

  const removeLogo = async (variant: LogoVariant) => {
    if (!token) return;
    setErr(null);
    setUploadingVariant(variant);
    try {
      const patchKey =
        variant === 'on_white' ? 'brand_logo_on_white_url' : 'brand_logo_on_primary_url';
      await tenantApi.branding.update({ [patchKey]: null });
      setLogoMeta((prev) => ({ ...prev, [variant]: null }));
      await refresh();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setUploadingVariant(null);
    }
  };

  if (!branding) {
    return (
      <div className="mx-auto max-w-4xl px-2 py-10">
        <div className="h-10 w-64 animate-pulse rounded bg-stone-100" />
        <div className="mt-6 h-4 w-96 animate-pulse rounded bg-stone-100" />
      </div>
    );
  }

  return (
    <div className="relative mx-auto max-w-5xl">
      {/* ── Hero ───────────────────────────────────────────── */}
      <header className="border-b border-stone-200 pb-10">
        <div className="text-[10px] font-medium uppercase tracking-[0.28em] text-stone-400">
          Inställningar
        </div>
        <h1
          className="mt-5 text-[52px] leading-[1.05] tracking-tight text-stone-900"
          style={{ fontFamily: SERIF, fontWeight: 400 }}
        >
          Visuell profil
        </h1>
        <p
          className="mt-4 max-w-xl text-[15px] leading-relaxed text-stone-500"
          style={{ fontFamily: SERIF, fontStyle: 'italic' }}
        >
          Ladda upp er logotyp så syns er organisations visuella identitet
          i Regta och på era dokument.
        </p>
        {!isOrgAdmin && (
          <div className="mt-6 inline-flex items-center gap-2 border-l-2 border-stone-300 pl-3 text-xs text-stone-500">
            Endast org_admin kan redigera. Du ser inställningarna i läsläge.
          </div>
        )}
        {err && (
          <div className="mt-6 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {err}
          </div>
        )}
      </header>

      {/* ── 01 Levereras av ─────────────────────────────────── */}
      <Section
        number="01"
        label="Partner"
        title="Levereras av"
        prose={
          <>
            Plattformen drivs av {branding.owner.name} på uppdrag av{' '}
            {branding.tenant.name ?? 'er organisation'}.
          </>
        }
      >
        <OwnerCard
          name={branding.owner.name}
          slug={branding.owner.slug}
          logoUrl={branding.owner.brandLogoOnWhiteUrl}
        />
      </Section>

      {/* ── 02 Hyresvärd ───────────────────────────────────── */}
      <Section
        number="02"
        label="Hyresvärd"
        title="Era identiteter"
        prose="Företaget eller föreningen som står som hyresvärd på era avtal. Ni kan spara flera och välja en standard — den fylls i automatiskt när ett nytt avtal skapas. Kan alltid ändras per avtal."
      >
        <SelfPartiesManager
          selfParties={selfPartiesQ.data?.self_parties ?? null}
          disabled={!isOrgAdmin}
          onChanged={async () => {
            await invalidate('settings.list_self_parties');
          }}
          onError={setErr}
          onSaved={flashSaved}
        />
      </Section>

      {/* ── 03 Granskning ──────────────────────────────────── */}
      <Section
        number="03"
        label="Granskning"
        title="Anpassad text"
        prose="Bestäm om avtal där texten har anpassats måste granskas innan de kan skickas för signering."
      >
        <ReviewPolicyToggle disabled={!isOrgAdmin} onError={setErr} onSaved={flashSaved} />
      </Section>

      {/* ── 04 Logotyp ─────────────────────────────────────── */}
      <Section
        number="04"
        label="Märke"
        title="Logotyp"
        prose="Ladda upp två versioner av er logotyp: en mörk (för vita ytor) och en ljus (för färgad bakgrund)."
      >
        <div className="space-y-10">
          {LOGO_VARIANTS.map((v) => {
            const url =
              v.variant === 'on_white'
                ? branding.tenant.brandLogoOnWhiteUrl
                : branding.tenant.brandLogoOnPrimaryUrl;
            const surfaceColor =
              v.variant === 'on_white'
                ? '#ffffff'
                : (branding.owner.brandColors?.primary ?? SIDEBAR_PRIMARY_FALLBACK);
            const ref = inputRefFor(v.variant);
            return (
              <div key={v.variant}>
                <div className="mb-3 flex items-baseline justify-between">
                  <div>
                    <div className="text-[10px] font-medium uppercase tracking-[0.28em] text-stone-400">
                      {v.title}
                    </div>
                    <p
                      className="mt-1 max-w-md text-[12px] leading-relaxed text-stone-500"
                      style={{ fontFamily: SERIF, fontStyle: 'italic' }}
                    >
                      {v.blurb}
                    </p>
                  </div>
                </div>
                <LogoPlate
                  logoUrl={url}
                  surfaceColor={surfaceColor}
                  variant={v.variant}
                  disabled={!isOrgAdmin || uploadingVariant === v.variant}
                  uploading={uploadingVariant === v.variant}
                  meta={logoMeta[v.variant]}
                  onPick={() => ref.current?.click()}
                  onDrop={(f) => handleFile(v.variant, f)}
                  onRemove={() => removeLogo(v.variant)}
                />
                <input
                  ref={ref}
                  type="file"
                  accept={ALLOWED_LOGO_TYPES.join(',')}
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void handleFile(v.variant, f);
                  }}
                />
              </div>
            );
          })}
        </div>
        <p className="mt-4 text-[11px] uppercase tracking-widest text-stone-400">
          PNG · JPG · WEBP · SVG &nbsp;·&nbsp; max {Math.round(MAX_LOGO_BYTES / 1024)} KB
        </p>
      </Section>

      {/* ── 05 Förhandsgranskning ──────────────────────────── */}
      <Section
        number="05"
        label="Förhandsgranskning"
        title="På dokumentet"
        prose="Så här syns märket i ett genererat avtal. Uppdateras direkt när ni ändrar något ovan."
        last
      >
        <LetterheadPreview
          ownerLogo={branding.owner.brandLogoOnWhiteUrl}
          ownerName={branding.owner.name}
          tenantLogo={branding.tenant.brandLogoOnWhiteUrl}
          tenantName={branding.tenant.name ?? null}
        />
      </Section>

      {/* Saved flash */}
      <div
        className={[
          'pointer-events-none fixed bottom-6 left-1/2 -translate-x-1/2 transition-opacity duration-300',
          savedFlash ? 'opacity-100' : 'opacity-0',
        ].join(' ')}
        aria-live="polite"
      >
        <div className="inline-flex items-center gap-2 rounded-full bg-stone-900 px-4 py-1.5 text-[11px] font-medium uppercase tracking-widest text-white">
          <Check className="h-3.5 w-3.5" />
          Sparat
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Layout primitives
// ──────────────────────────────────────────────────────────────────────

function Section({
  number,
  label,
  title,
  prose,
  children,
  last,
}: {
  number: string;
  label: string;
  title: string;
  prose: ReactNode;
  children: ReactNode;
  last?: boolean;
}) {
  return (
    <section
      className={[
        'grid grid-cols-1 gap-12 py-14 md:grid-cols-[260px_1fr]',
        last ? '' : 'border-b border-stone-200',
      ].join(' ')}
    >
      <div className="md:pt-1">
        <div
          className="text-[11px] font-medium tracking-[0.3em] text-stone-300"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {number}
        </div>
        <div className="mt-3 text-[10px] font-medium uppercase tracking-[0.28em] text-stone-400">
          {label}
        </div>
        <h2
          className="mt-2 text-[26px] leading-tight text-stone-900"
          style={{ fontFamily: SERIF, fontWeight: 400 }}
        >
          {title}
        </h2>
        <p
          className="mt-3 max-w-[24ch] text-[13px] leading-relaxed text-stone-500"
          style={{ fontFamily: SERIF, fontStyle: 'italic' }}
        >
          {prose}
        </p>
      </div>
      <div>{children}</div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Granskning policy (ADR-0102 decision 8)
// ──────────────────────────────────────────────────────────────────────

function ReviewPolicyToggle({
  disabled,
  onError,
  onSaved,
}: {
  disabled: boolean;
  onError: (message: string | null) => void;
  onSaved: () => void;
}) {
  const policyQ = useTenantQuery('settings.get_review_policy', () => tenantApi.settings.get_review_policy(), [], {
    ignoreActiveGroup: true,
  });
  const invalidate = useInvalidate();
  const [saving, setSaving] = useState(false);
  const current = policyQ.data?.require_review_for_deviating_documents ?? null;

  const toggle = async (next: boolean) => {
    onError(null);
    setSaving(true);
    try {
      await tenantApi.settings.set_review_policy({ require_review_for_deviating_documents: next });
      await invalidate('settings.get_review_policy');
      onSaved();
    } catch (ex) {
      onError((ex as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (policyQ.error) {
    return <p className="text-sm text-stone-500">Inställningen kunde inte läsas.</p>;
  }
  return (
    <label
      className={
        'flex max-w-xl items-start gap-3 rounded border border-stone-200 bg-white p-4 ' +
        (disabled || current === null ? 'cursor-default' : 'cursor-pointer')
      }
    >
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 accent-stone-900"
        checked={current === true}
        disabled={disabled || saving || current === null}
        onChange={(e) => void toggle(e.target.checked)}
      />
      <span className="flex flex-col gap-1">
        <span className="text-sm font-medium text-stone-900">Kräv granskning för avtal med anpassad text</span>
        <span className="text-[12px] leading-relaxed text-stone-500">
          När texten i ett avtal avviker från standardmallen måste avtalet granskas och godkännas innan det kan
          skickas för signering eller exporteras. Ändras avtalet efter godkännandet behöver det granskas igen.
        </span>
      </span>
    </label>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Owner card (read-only)
// ──────────────────────────────────────────────────────────────────────

function OwnerCard({
  name,
  slug,
  logoUrl,
}: {
  name: string;
  slug: string;
  logoUrl: string | null;
}) {
  return (
    <div className="flex items-center gap-6 border border-stone-200 bg-stone-50/60 p-6">
      <div className="flex h-14 min-w-32 items-center justify-center bg-white px-4 shadow-sm">
        {logoUrl ? (
          <img src={logoUrl} alt={name} className="max-h-10 object-contain" />
        ) : (
          <span className="text-sm font-medium text-stone-700">{name}</span>
        )}
      </div>
      <div className="flex-1">
        <div
          className="text-lg text-stone-900"
          style={{ fontFamily: SERIF, fontWeight: 500 }}
        >
          {name}
        </div>
        <div className="mt-0.5 text-xs text-stone-500">
          <span className="font-mono">{slug}.regta.se</span>
        </div>
      </div>
      <div className="text-[10px] uppercase tracking-widest text-stone-400">
        Endast läsbart
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Logo plate — drop zone + filled state
// ──────────────────────────────────────────────────────────────────────

function LogoPlate({
  logoUrl,
  surfaceColor,
  variant,
  disabled,
  uploading,
  meta,
  onPick,
  onDrop,
  onRemove,
}: {
  logoUrl: string | null;
  /** Background color the plate uses so we preview the logo on its real surface. */
  surfaceColor: string;
  variant: LogoVariant;
  disabled: boolean;
  uploading: boolean;
  meta: LogoMeta | null;
  onPick: () => void;
  onDrop: (file: File) => void;
  onRemove: () => void;
}) {
  const [dragOver, setDragOver] = useState(false);
  // Text/border colors flip when the plate is on a dark surface.
  const onDark = variant === 'on_primary';
  const subtleText = onDark ? 'text-white/40' : 'text-stone-300';
  const cornerLabel = onDark ? 'text-white/50' : 'text-stone-300';
  const plateBorder = onDark ? 'border-transparent' : 'border-stone-200';

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const f = e.dataTransfer.files?.[0];
    if (f) onDrop(f);
  };

  if (logoUrl) {
    return (
      <div>
        <div
          className={`relative flex h-44 items-center justify-center border px-12 ${plateBorder}`}
          style={{ backgroundColor: surfaceColor }}
        >
          <img
            src={logoUrl}
            alt="Logotyp"
            className="max-h-24 max-w-full object-contain"
          />
          <div className={`absolute left-3 top-3 text-[9px] uppercase tracking-widest ${cornerLabel}`}>
            {variant === 'on_white' ? 'På vit' : 'På primär'}
          </div>
          <div className={`absolute right-3 top-3 text-[9px] uppercase tracking-widest ${cornerLabel}`}>
            Aktuell
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-stone-100 pt-3 text-xs text-stone-500">
          <div className="font-mono text-[11px]">
            {meta
              ? `${meta.type.split('/')[1]?.toUpperCase()} · ${formatBytes(meta.bytes)}`
              : 'Sparat märke'}
          </div>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={onPick}
              disabled={disabled}
              className="text-[11px] uppercase tracking-widest text-stone-500 hover:text-stone-900 disabled:opacity-40"
            >
              Byt fil
            </button>
            <button
              type="button"
              onClick={onRemove}
              disabled={disabled}
              className="text-[11px] uppercase tracking-widest text-stone-500 hover:text-red-700 disabled:opacity-40"
            >
              Ta bort
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => !disabled && onPick()}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled) onPick();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      style={{ backgroundColor: surfaceColor }}
      className={[
        'relative flex h-44 cursor-pointer flex-col items-center justify-center border px-6 transition-all',
        disabled ? 'cursor-not-allowed opacity-60' : '',
        onDark
          ? dragOver
            ? 'border-white/60'
            : 'border-white/20 border-dashed hover:border-white/40'
          : dragOver
            ? 'border-stone-900 bg-white'
            : 'border-stone-300 border-dashed hover:bg-stone-50/80',
      ].join(' ')}
    >
      <div className={`absolute left-3 top-3 text-[9px] uppercase tracking-widest ${subtleText}`}>
        {variant === 'on_white' ? 'På vit' : 'På primär'}
      </div>
      <div className={`absolute right-3 top-3 text-[9px] uppercase tracking-widest ${subtleText}`}>
        16 : 5
      </div>
      {uploading ? (
        <Loader2 className={`h-6 w-6 animate-spin ${onDark ? 'text-white/60' : 'text-stone-400'}`} />
      ) : (
        <ImagePlus className={`h-7 w-7 ${onDark ? 'text-white/70' : 'text-stone-400'}`} strokeWidth={1.25} />
      )}
      <div
        className={`mt-4 text-base ${onDark ? 'text-white' : 'text-stone-700'}`}
        style={{ fontFamily: SERIF }}
      >
        {dragOver ? 'Släpp filen här' : 'Dra in er logotyp'}
      </div>
      <div className={`mt-1 text-[11px] uppercase tracking-widest ${onDark ? 'text-white/60' : 'text-stone-400'}`}>
        eller klicka för att välja
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Self-parties manager — multi-row list + inline create/edit
// ──────────────────────────────────────────────────────────────────────

function addressSummary(sp: SelfParty): string {
  const place = [sp.postalCode, sp.city].filter(Boolean).join(' ');
  return [sp.orgNumber, sp.addressLine1, place, sp.email, sp.phone].filter(Boolean).join(' · ');
}

function SelfPartiesManager({
  selfParties,
  disabled,
  onChanged,
  onError,
  onSaved,
}: {
  /** null while loading; [] once loaded with none saved yet. */
  selfParties: SelfParty[] | null;
  /** True for non-admins — list shows read-only, no add/edit/promote. */
  disabled: boolean;
  onChanged: () => Promise<void> | void;
  onError: (msg: string | null) => void;
  onSaved: () => void;
}) {
  // null = no form open; 'new' = create; otherwise the id being edited.
  const [editing, setEditing] = useState<'new' | string | null>(null);
  const [form, setForm] = useState<SelfPartyForm>(EMPTY_SELF_PARTY);
  const [busy, setBusy] = useState(false);

  const openNew = () => {
    setForm(EMPTY_SELF_PARTY);
    setEditing('new');
  };
  const openEdit = (sp: SelfParty) => {
    setForm(selfPartyToForm(sp));
    setEditing(sp.id);
  };
  const cancel = () => {
    setEditing(null);
    setForm(EMPTY_SELF_PARTY);
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) {
      onError('Namn krävs.');
      return;
    }
    setBusy(true);
    onError(null);
    try {
      const payload = {
        label: form.label.trim() || null,
        name,
        org_number: form.orgNumber.trim() || null,
        address_line1: form.addressLine1.trim() || null,
        address_line2: form.addressLine2.trim() || null,
        postal_code: form.postalCode.trim() || null,
        city: form.city.trim() || null,
        country: form.country.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
      };
      if (editing === 'new') {
        await tenantApi.settings.create_self_party(payload);
      } else if (editing) {
        await tenantApi.settings.update_self_party({ id: editing, ...payload });
      }
      cancel();
      await onChanged();
      onSaved();
    } catch (ex) {
      onError((ex as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const setDefault = async (sp: SelfParty) => {
    if (sp.isDefault) return;
    setBusy(true);
    onError(null);
    try {
      await tenantApi.settings.set_default_self_party({ id: sp.id });
      await onChanged();
      onSaved();
    } catch (ex) {
      onError((ex as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!selfParties) {
    return (
      <div className="space-y-3">
        <div className="h-16 animate-pulse rounded border border-stone-200 bg-stone-100" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {selfParties.length === 0 && editing === null && (
        <div className="border border-dashed border-stone-300 bg-stone-50/40 p-6 text-sm text-stone-500">
          Ingen identitet sparad ännu. Era avtal fyller inte i någon hyresvärd
          förrän ni lägger till en.
        </div>
      )}

      {selfParties.length > 0 && (
        <ul className="space-y-2">
          {selfParties.map((sp) => (
            <li
              key={sp.id}
              className="flex flex-wrap items-start justify-between gap-3 border border-stone-200 bg-white p-4"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className="text-base text-stone-900"
                    style={{ fontFamily: SERIF, fontWeight: 500 }}
                  >
                    {sp.name}
                  </span>
                  {sp.isDefault && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-stone-900 px-2 py-0.5 text-[9px] font-medium uppercase tracking-widest text-white">
                      <Star className="h-2.5 w-2.5" />
                      Standard
                    </span>
                  )}
                </div>
                <div className="mt-1 text-xs text-stone-500">
                  {addressSummary(sp) || '—'}
                </div>
              </div>
              {!disabled && editing === null && (
                <div className="flex shrink-0 items-center gap-3">
                  {!sp.isDefault && (
                    <button
                      type="button"
                      onClick={() => void setDefault(sp)}
                      disabled={busy}
                      className="text-[11px] uppercase tracking-widest text-stone-500 hover:text-stone-900 disabled:opacity-40"
                    >
                      Gör till standard
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => openEdit(sp)}
                    className="text-[11px] uppercase tracking-widest text-stone-500 hover:text-stone-900"
                  >
                    Redigera
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {editing !== null ? (
        <div className="space-y-4">
          <SelfPartyForm form={form} disabled={busy} onChange={setForm} />
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy || form.name.trim().length === 0}
              className="inline-flex items-center gap-2 bg-stone-900 px-5 py-2.5 text-xs font-medium uppercase tracking-widest text-white hover:bg-stone-800 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              {editing === 'new' ? 'Lägg till identitet' : 'Spara ändringar'}
            </button>
            <button
              type="button"
              onClick={cancel}
              disabled={busy}
              className="text-xs uppercase tracking-widest text-stone-500 hover:text-stone-900"
            >
              Avbryt
            </button>
          </div>
        </div>
      ) : (
        !disabled && (
          <button
            type="button"
            onClick={openNew}
            className="inline-flex items-center gap-2 border border-stone-300 px-4 py-2 text-xs font-medium uppercase tracking-widest text-stone-700 hover:border-stone-900 hover:text-stone-900"
          >
            <Plus className="h-3.5 w-3.5" />
            Lägg till identitet
          </button>
        )
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Self-party form — minimal editorial pattern matching the page tone
// ──────────────────────────────────────────────────────────────────────

function SelfPartyForm({
  form,
  disabled,
  onChange,
}: {
  form: SelfPartyForm;
  disabled: boolean;
  onChange: (next: SelfPartyForm) => void;
}) {
  const field = (key: keyof SelfPartyForm) => ({
    value: form[key],
    disabled,
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange({ ...form, [key]: e.target.value }),
  });
  return (
    <div className="space-y-6 border border-stone-200 bg-stone-50/40 p-6">
      <Labeled label="Namn" required>
        <input
          type="text"
          placeholder="Hyresgäst AB"
          {...field('name')}
          className="w-full border-0 border-b border-stone-300 bg-transparent py-2 text-base text-stone-900 focus:border-stone-900 focus:outline-none disabled:opacity-50"
          style={{ fontFamily: SERIF }}
        />
      </Labeled>
      <Labeled label="Org./pers.nr">
        <input
          type="text"
          placeholder="559123-4567"
          {...field('orgNumber')}
          className="w-full border-0 border-b border-stone-300 bg-transparent py-2 font-mono text-sm text-stone-900 focus:border-stone-900 focus:outline-none disabled:opacity-50"
        />
      </Labeled>
      <Labeled label="Gatuadress">
        <input
          type="text"
          placeholder="Storgatan 1"
          {...field('addressLine1')}
          className="w-full border-0 border-b border-stone-300 bg-transparent py-2 text-sm text-stone-700 focus:border-stone-900 focus:outline-none disabled:opacity-50"
        />
      </Labeled>
      <Labeled label="Adressrad 2" hint="valfritt — t.ex. c/o eller våningsplan">
        <input
          type="text"
          placeholder="c/o …"
          {...field('addressLine2')}
          className="w-full border-0 border-b border-stone-300 bg-transparent py-2 text-sm text-stone-700 focus:border-stone-900 focus:outline-none disabled:opacity-50"
        />
      </Labeled>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-[1fr_2fr]">
        <Labeled label="Postnummer">
          <input
            type="text"
            placeholder="111 23"
            {...field('postalCode')}
            className="w-full border-0 border-b border-stone-300 bg-transparent py-2 text-sm text-stone-700 focus:border-stone-900 focus:outline-none disabled:opacity-50"
          />
        </Labeled>
        <Labeled label="Ort">
          <input
            type="text"
            placeholder="Stockholm"
            {...field('city')}
            className="w-full border-0 border-b border-stone-300 bg-transparent py-2 text-sm text-stone-700 focus:border-stone-900 focus:outline-none disabled:opacity-50"
          />
        </Labeled>
      </div>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <Labeled label="E-post" hint="kontakt — fylls i på avtalen">
          <input
            type="email"
            placeholder="info@foretag.se"
            {...field('email')}
            className="w-full border-0 border-b border-stone-300 bg-transparent py-2 text-sm text-stone-700 focus:border-stone-900 focus:outline-none disabled:opacity-50"
          />
        </Labeled>
        <Labeled label="Telefon">
          <input
            type="tel"
            placeholder="08-123 456 78"
            {...field('phone')}
            className="w-full border-0 border-b border-stone-300 bg-transparent py-2 text-sm text-stone-700 focus:border-stone-900 focus:outline-none disabled:opacity-50"
          />
        </Labeled>
      </div>
    </div>
  );
}

function Labeled({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between">
        <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-stone-400">
          {label}
          {required ? <span className="ml-1 text-stone-300">·</span> : null}
        </span>
        {hint ? <span className="text-[10px] text-stone-400">{hint}</span> : null}
      </div>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
